import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';

const BatchList = () => {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');

  const navigate = useNavigate();

  useEffect(() => {
    fetchBatches();
  }, [statusFilter]);

  const fetchBatches = async () => {
    try {
      setLoading(true);
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const response = await api.get(`/api/batches/${params}`);

      setBatches(Array.isArray(response.data) ? response.data : []);

      setError(null);
    } catch (err) {
      setError('Failed to load batches');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Sorting logic
  const handleSort = (field) => {
    if (sortField === field) {
      // Same column clicked — toggle direction
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // New column — default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIndicator = (field) => {
    if (sortField !== field) return ' ↕';
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  const STATUS_ORDER = [
    'DRAFT',
    'PENDING_APPROVAL',
    'APPROVED',
    'REJECTED',
    'PAYMENT_PROCESSING',
    'PAYMENT_COMPLETED',
  ];

  const sortedBatches = [...batches].sort((a, b) => {
    let valA, valB;

    switch (sortField) {
      case 'description':
        valA = (a.description || '').toLowerCase();
        valB = (b.description || '').toLowerCase();
        break;
      case 'payment_date':
        valA = a.payment_date || '';
        valB = b.payment_date || '';
        break;
      case 'status':
        valA = STATUS_ORDER.indexOf(a.status);
        valB = STATUS_ORDER.indexOf(b.status);
        break;
      case 'created_at':
        valA = a.created_at || '';
        valB = b.created_at || '';
        break;
      default:
        return 0;
    }

    if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
    if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const getStatusBadge = (status) => {
    const statusStyles = {
      DRAFT: { backgroundColor: '#6b7280', color: 'white' },
      PENDING_APPROVAL: { backgroundColor: '#f59e0b', color: 'white' },
      APPROVED: { backgroundColor: '#10b981', color: 'white' },
      REJECTED: { backgroundColor: '#ef4444', color: 'white' },
      PAYMENT_PROCESSING: { backgroundColor: '#3b82f6', color: 'white' },
      PAYMENT_COMPLETED: { backgroundColor: '#6366f1', color: 'white' },
    };

    const statusLabels = {
      DRAFT: 'Draft',
      PENDING_APPROVAL: 'Pending Approval',
      APPROVED: 'Approved',
      REJECTED: 'Rejected',
      PAYMENT_PROCESSING: 'Payment Processing',
      PAYMENT_COMPLETED: 'Payment Completed',
    };

    return (
      <span
        style={{
          ...statusStyles[status],
          padding: '4px 12px',
          borderRadius: '9999px',
          fontSize: '12px',
          fontWeight: '500',
          whiteSpace: 'nowrap',
        }}
      >
        {statusLabels[status] || status}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const formatAmount = (amount, currency = 'ZAR') => {
    if (!amount) return '-';
    const symbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency === 'GBP' ? '£' : 'R';
    return `${symbol} ${parseFloat(amount).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`;
  };

  const renderTotal = (batch) => {
    const ct = batch.currency_totals;
    
    // No currency_totals data (fallback for old data)
    if (!ct) {
      return (
        <div className="currency-breakdown-inline">
          <div className="breakdown-row">
            <span className="breakdown-currency-tag">ZAR</span>
            <span>{formatAmount(batch.total_amount, 'ZAR')}</span>
          </div>
        </div>
      );
    }
    
    // No payments
    if (!ct.totals || ct.totals.length === 0) {
      return '-';
    }
    
    // Always show with currency badge (single or multiple)
    return (
      <div className="currency-breakdown-inline">
        {ct.totals.map((item, index) => (
          <div key={index} className="breakdown-row">
            <span className="breakdown-currency-tag">{item.currency}</span>
            <span>{formatAmount(item.total, item.currency)}</span>
          </div>
        ))}
      </div>
    );
  };

  if (loading) return <div className="loading">Loading batches...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="batch-list-container">
      <div className="batch-list-header">
        <h1>Payment Batches</h1>
        <button
          className="btn btn-primary"
          onClick={() => navigate('/batches/new')}
        >
          + New Batch
        </button>
      </div>

      <div className="filters">
        <label>Filter by Status:</label>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All Statuses</option>
          <option value="DRAFT">Draft</option>
          <option value="PENDING_APPROVAL">Pending Approval</option>
          <option value="APPROVED">Approved</option>
          <option value="REJECTED">Rejected</option>
          <option value="PAYMENT_PROCESSING">Payment Processing</option>
          <option value="PAYMENT_COMPLETED">Payment Completed</option>
        </select>
      </div>

      {batches.length === 0 ? (
        <div className="empty-state">
          <p>No batches found</p>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/batches/new')}
          >
            + Create a batch
          </button>
        </div>
      ) : (
        <table className="batch-table">
          <thead>
            <tr>
              <th>Reference</th>
              <th className="sortable" onClick={() => handleSort('description')}>
                Description{getSortIndicator('description')}
              </th>
              <th className="sortable" onClick={() => handleSort('payment_date')}>
                Payment Date{getSortIndicator('payment_date')}
              </th>
              <th>Payments</th>
              <th>Total</th>
              <th className="sortable" onClick={() => handleSort('status')}>
                Status{getSortIndicator('status')}
              </th>
              <th className="sortable" onClick={() => handleSort('created_at')}>
                Created{getSortIndicator('created_at')}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedBatches.map((batch) => (
              <tr key={batch.id}>
                <td className="reference">{batch.batch_reference}</td>
                <td>{batch.description || '-'}</td>
                <td>{formatDate(batch.payment_date)}</td>
                <td className="centered">
                  {batch.payment_count}
                  {batch.approved_count > 0 && (
                    <span className={batch.approved_count < batch.payment_count ? 'partial-approved-count' : 'approved-count'}>
                      ({batch.approved_count} approved)
                    </span>
                  )}
                </td>
                <td className="amount">{renderTotal(batch)}</td>
                <td>{getStatusBadge(batch.status)}</td>
                <td>{formatDate(batch.created_at)}</td>
                <td className="actions">
                  <button
                    className="btn btn-small"
                    onClick={() => navigate(`/batches/${batch.id}`)}
                  >
                    View
                  </button>
                  {(batch.status === 'APPROVED' || batch.status === 'PAYMENT_PROCESSING' || batch.status === 'PAYMENT_COMPLETED') && (
                    <button
                      className="btn btn-small btn-success"
                      onClick={() => navigate(`/batches/${batch.id}/schedule`)}
                    >
                      Payment Schedule
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default BatchList;